import { Injectable, Inject, PLATFORM_ID, InjectionToken, Optional } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';

export interface ClientConfig {
  brokerId: string;
  themeClass: string;
}

export const REQUEST = new InjectionToken<any>('REQUEST');

@Injectable({
  providedIn: 'root'
})
export class ClientConfigService {
  private currentConfig: ClientConfig;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(REQUEST) private request: any
  ) {
    this.currentConfig = this.determineClientConfig();
    this.applyStyles();
  }

  private determineClientConfig(): ClientConfig {
    // If running in SSR we fallback to default if we can't determine the host from request
    if (!isPlatformBrowser(this.platformId)) {
      const host = this.request?.headers?.host || '';

      if (environment.activeClientOverride && (host.includes('localhost') || host.includes('127.0.0.1'))) {
        return environment.clients[environment.activeClientOverride] || environment.clients['chill'];
      }

      if (host.includes('quotedevil.ie')) {
        return environment.clients['quotedevil'];
      }

      return environment.clients[environment.activeClientOverride || 'chill'];
    }

    const host = window.location.hostname;

    // Developer override locally
    if (environment.activeClientOverride && (host === 'localhost' || host === '127.0.0.1')) {
      return environment.clients[environment.activeClientOverride] || environment.clients['chill'];
    }

    // Match by host
    if (host.includes('quotedevil.ie')) {
      return environment.clients['quotedevil'];
    }

    // Default fallback
    return environment.clients['chill'];
  }

  get config(): ClientConfig {
    return this.currentConfig;
  }

  getBrokerId(): string {
    return this.currentConfig.brokerId;
  }

  getThemeClass(): string {
    return this.currentConfig.themeClass;
  }

  getSupportPhone(): string {
    return this.getBrokerId() === '04' ? '01 254 1300' : '014 003 494';
  }

  getSupportLink(): string {
    return this.getBrokerId() === '04' ? 'https://www.quotedevil.ie/customer-care' : 'https://www.chill.ie/contact-us/';
  }

  applyStyles() {
    if (this.currentConfig.themeClass === 'theme-quotedevil') {
      this.applyQuoteDevilStyle();
    } else {
      this.applyChillStyle();
    }
  }

  private applyChillStyle() {
    if (!isPlatformBrowser(this.platformId)) return;
    document.title = 'Chill Onboarding';
    const favicon = document.querySelector('link[rel*="icon"]') as HTMLLinkElement;
    if (favicon) {
      favicon.href = 'favicon.ico?v=' + environment.appVersion;
    }

    document.documentElement.style.setProperty('--primary-color', '#5a2293');
    document.documentElement.style.setProperty('--primary-color-light', '#5a2293');
    document.documentElement.style.setProperty('--primary-color-dark', '#5a2293');
    document.documentElement.style.setProperty('--secondary-color', '#e5dff1');
    document.documentElement.style.setProperty('--secondary-color-light', '#f4f0fa');
    document.documentElement.style.setProperty('--secondary-color-dark', '#3b1660');
    document.documentElement.style.setProperty('--action-color', '#a3c63a');
    document.documentElement.style.setProperty('--link-color', '#23bcd8');
  }

  private applyQuoteDevilStyle() {
    if (!isPlatformBrowser(this.platformId)) return;
    document.title = 'Quote Devil Onboarding';
    const favicon = document.querySelector('link[rel*="icon"]') as HTMLLinkElement;
    if (favicon) {
      favicon.href = 'assets/images/faviconQD.ico?v=' + environment.appVersion;
    }

    document.documentElement.style.setProperty('--primary-color', '#AB1C21');
    document.documentElement.style.setProperty('--primary-color-light', '#AB1C21');
    document.documentElement.style.setProperty('--primary-color-dark', '#AB1C21');
    document.documentElement.style.setProperty('--secondary-color', '#3A3A3A');
    document.documentElement.style.setProperty('--secondary-color-light', '#3A3A3A');
    document.documentElement.style.setProperty('--secondary-color-dark', '#3A3A3A');
    document.documentElement.style.setProperty('--action-color', '#AB1C21');
    document.documentElement.style.setProperty('--link-color', '#000000');
  }
}
